import React, { useState, useEffect } from 'react';
import { Select, Typography, Switch, Space, Tooltip, Alert, Card, Divider, List, Tag, Row, Col, Collapse } from 'antd';
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip, Legend as RechartsLegend, ResponsiveContainer } from 'recharts';
import { InfoCircleOutlined, CaretRightOutlined, DownOutlined, RightOutlined } from '@ant-design/icons';
import './ResultsTable.css';
import moment from 'moment';
import { STUDENT_LOAN_PLAN_NAMES } from '../constants/taxConstants';
import PensionContributionInput from './PensionContributionInput';
import { useWizardStore } from '../features/wizard/store'

const { Option } = Select;
const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

const ResultsTable = ({ inputValues, selectedTaxYear, rsuTaxRate = 50 }) => {
  const [selectedPensionPercentage, setSelectedPensionPercentage] = useState(0);
  const [results, setResults] = useState(null);
  const rsuEnabled = useWizardStore((s) => s.rsuEnabled)
  const setRsuEnabled = useWizardStore((s) => s.setRsuEnabled)
  const [includeRSU, setIncludeRSU] = useState(rsuEnabled ?? true);
  const [expandedItems, setExpandedItems] = useState([]);
  const [calculationResults, setCalculationResults] = useState(null);

  // Fix: Get isTaxResidentInScotland from inputValues
  const isTaxResidentInScotland = inputValues?.isTaxResidentInScotland || false;

  const toggleExpand = (index) => {
    setExpandedItems(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };  

  useEffect(() => {
    // keep local toggle in sync with global store if it changes elsewhere
    setIncludeRSU(rsuEnabled ?? true)
  }, [rsuEnabled])

  useEffect(() => {
    if (inputValues) {
      if (inputValues.employeePensionContribution !== undefined) {
        setSelectedPensionPercentage(parseFloat(inputValues.employeePensionContribution));
      }
      // Import calculation function - in a real implementation, you'd use the actual function
      import('../salaryCalculations').then(({ calculateSalaryDetails }) => {
        // Log the pension percentage we're calculating with
          if (process.env.NODE_ENV !== 'production') {
            console.log('ResultsTable: Calculating with pension %:', selectedPensionPercentage);
          }
        
        const calculatedResults = calculateSalaryDetails({
          ...inputValues,
          // Use employeePensionContributionPct as the parameter name
          employeePensionContributionPct: selectedPensionPercentage
        }, selectedTaxYear);
        
        // Find the item in the results array with the matching pension percentage
        // Use a tolerance for floating point comparison
        const resultIndex = calculatedResults.findIndex(
          item => Math.abs(parseFloat(item.employee_pension_contribution_pct) - selectedPensionPercentage) < 0.1
        );
        
        // If found, use that result
        if (resultIndex !== -1) {
          if (process.env.NODE_ENV !== 'production') {
            console.log('ResultsTable: Found matching result at index', resultIndex);
          }
          setResults(calculatedResults[resultIndex]);
          setCalculationResults(calculatedResults); // Store all results
        } else {
          console.error('ResultsTable: No matching result found for pension %:', selectedPensionPercentage);
          // Log the available percentages to debug
          if (process.env.NODE_ENV !== 'production') {
            console.log('Available percentages:', calculatedResults.map(r => r.employee_pension_contribution_pct));
          }
        }
      });
    }
  }, [inputValues, selectedPensionPercentage, selectedTaxYear, rsuTaxRate]);

  // propagate toggle changes to global store
  const handleToggleIncludeRsu = (val) => {
    setIncludeRSU(val)
    try { setRsuEnabled(val) } catch (_) {}
  }

  // Update the formatCurrency function to handle undefined values properly
  const formatCurrency = (value) => {
    // Handle undefined or null values
    if (value === undefined || value === null) {
      return '£0.00';
    }
    
    // If it's a string and ends with %, return it as is
    if (typeof value === 'string' && value.endsWith('%')) {
      return value;
    }
    
    // Check if the value is NaN and return a valid string
    if (isNaN(value)) {
      return value.toString();
    }
    
    // Convert negative values to positive for display, but keep the minus sign
    const isNegative = value < 0;
    const absValue = Math.abs(value);
    
    const formatted = new Intl.NumberFormat('en-GB', { 
      style: 'currency', 
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(absValue);
    
    // For refund values in RSU tax calculations, we want to show them as positive with a plus sign
    return isNegative ? `−${formatted}` : formatted;
  };

  const getPositiveNegativeClass = (value) => {
    if (typeof value !== 'number') return '';
    return value > 0 ? 'positive-value' : value < 0 ? 'negative-value' : '';
  };

  if (!results) return null;

  // Main calculations
  const grossPay = includeRSU 
    ? results.base_salary + results.bonus + results.rsus
    : results.base_salary + results.bonus;
    
  const taxableIncome = includeRSU
    ? parseFloat(results.taxable_income_less_pension)
    : parseFloat(results.taxable_income_less_pension_excl_rsus);
    
  const incomeTax = includeRSU
    ? parseFloat(results.total_tax)
    : parseFloat(results.total_tax_excl_rsus);
    
  const nic = includeRSU
    ? parseFloat(results.total_nic)
    : parseFloat(results.total_nic_excl_rsus);
  
  // Student loan calculations
  const studentLoanRepayments = includeRSU
    ? parseFloat(results.student_loan_repayments || 0)
    : parseFloat(results.student_loan_repayments_excl_rsus || 0);

  const hasStudentLoans = studentLoanRepayments > 0;

  // Get the student loan breakdown from the results
  const studentLoanBreakdown = includeRSU
    ? (results.student_loan_breakdown || {})
    : (results.student_loan_breakdown_excl_rsus || {});
    
  const totalDeductions = incomeTax + nic + studentLoanRepayments;
  
  const netSalary = includeRSU
    ? parseFloat(results.net_salary)
    : parseFloat(results.net_salary_excl_rsu);
    
  const netSalaryPlusPension = includeRSU
    ? parseFloat(results.net_salary_inc_pension)
    : parseFloat(results.net_salary_inc_pension_ex_RSU);

  const rsuAfterEmployerNIC = results.rsus * (1 - inputValues.rsuemployerNICrate / 100);
  
  const personalAllowance = includeRSU
    ? parseFloat(results.personal_allowance_reduction)
    : parseFloat(results.personal_allowance_reduction_excl_rsus);

  // Fix: Calculate rsuTaxAdjustment
  const rsuTaxAdjustment = includeRSU 
    ? parseFloat(results.rsu_additional_tax_due || 0) - parseFloat(results.rsu_refund_due || 0)
    : 0;

  // Pie chart data for high-level composition
  const employeePension = parseFloat(results.employee_pension_amount || 0);
  const pieData = [
    { name: 'Net Salary', value: Math.max(0, netSalary) },
    { name: 'Income Tax', value: Math.max(0, incomeTax) },
    { name: 'National Insurance', value: Math.max(0, nic) },
    ...(hasStudentLoans ? [{ name: 'Student Loans', value: Math.max(0, studentLoanRepayments) }] : []),
    ...(employeePension > 0 ? [{ name: 'Employee Pension', value: employeePension }] : []),
  ];
  const pieDataFiltered = pieData.filter(d => d.value > 0);

  // Pastel palette
  const PIE_COLOURS = {
    'Net Salary': '#A5D8FF',        // pastel blue
    'Income Tax': '#FFADAD',        // pastel red
    'National Insurance': '#FFD6A5',// pastel orange
    'Student Loans': '#CDB4DB',     // pastel purple
    'Employee Pension': '#B9FBC0',  // pastel green
  };

  const pieTotal = pieData.reduce((sum, d) => sum + (isFinite(d.value) ? d.value : 0), 0);

  const RADIAN = Math.PI / 180;
  const renderPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const r = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + r * Math.cos(-midAngle * RADIAN);
    const y = cy + r * Math.sin(-midAngle * RADIAN);
    return (
      <text x={x} y={y} fill="#111827" textAnchor="middle" dominantBaseline="central" style={{ fontSize: 11 }}>
        {`${Math.round(percent * 100)}%`}
      </text>
    );
  };

  const CustomPieTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const name = payload[0].name;
    let rsu = 0, non = 0;
    if (name === 'Income Tax') {
      rsu = parseFloat(results.rsu_tax || 0);
      non = parseFloat(results.non_rsu_tax || 0);
    } else if (name === 'National Insurance') {
      const total = parseFloat(results.total_nic || 0);
      const excl = parseFloat(results.total_nic_excl_rsus || 0);
      rsu = Math.max(0, total - excl);
      non = excl;
    } else if (name === 'Student Loans') {
      const excl = parseFloat(results.student_loan_repayments_excl_rsus || 0);
      rsu = Math.max(0, studentLoanRepayments - excl);
      non = excl;
    } else if (name === 'Net Salary') {
      const excl = parseFloat(results.net_salary_excl_rsu || 0);
      rsu = Math.max(0, netSalary - excl);
      non = excl;
    } else if (name === 'Employee Pension') {
      // No RSU/Non-RSU split for pension
      const total = parseFloat(results.employee_pension_amount || 0);
      return (
        <div style={{ background: '#fff', border: '1px solid #eee', padding: 8, borderRadius: 6 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>{name}</div>
          <div>Total: {formatCurrency(total)}</div>
        </div>
      );
    }
    return (
      <div style={{ background: '#fff', border: '1px solid #eee', padding: 8, borderRadius: 6 }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>{name}</div>
        <div>RSU-related: {formatCurrency(rsu)}</div>
        <div>Non-RSU: {formatCurrency(non)}</div>
        <Divider style={{ margin: '6px 0' }} />
        <div>Total: {formatCurrency((isFinite(rsu) ? rsu : 0) + (isFinite(non) ? non : 0))}</div>
      </div>
    );
  };

  // Create formatted sections
  const formatListItem = (label, annual, monthly, className = '', explanation = '') => {
    return {
      label,
      annual,
      monthly: monthly || annual / 12,
      className,
      explanation
    };
  };

  const getIncomeTaxBreakdown = () => {
    if (inputValues.isTaxResidentInScotland) {
      return [
        formatListItem('Starter Rate (19%)', parseFloat(results.tax_starter_rate || 0)),
        formatListItem('Basic Rate (20%)', parseFloat(results.tax_basic_rate || 0)),
        formatListItem('Intermediate Rate (21%)', parseFloat(results.tax_intermediate_rate || 0)),
        formatListItem('Higher Rate (42%)', parseFloat(results.tax_higher_rate || 0)),
        formatListItem('Top Rate (48%)', parseFloat(results.tax_top_rate || 0)),
      ];
    } else {
      return [
        formatListItem('Basic Rate (20%)', parseFloat(results.tax_basic_rate || 0)),
        formatListItem('Higher Rate (40%)', parseFloat(results.tax_higher_rate || 0)),
        formatListItem('Additional Rate (45%)', parseFloat(results.tax_additional_rate || 0)),
      ];
    }
  };

  const getRsuTaxSummary = () => {
    if (!includeRSU) return [];

      // Use the results field for employer NIC status
    const isEmployerNicApplied = results.rsu_employer_nic_applied;
    const employerNicRate = results.rsu_employer_nic_rate || inputValues.rsuemployerNICrate;

    const employerNicExplanation = isEmployerNicApplied
      ? `Amount after deducting ${employerNicRate}% employer NIC (paid by employee)`
      : `Full amount (employer NIC not deducted)`;

    return [
      formatListItem('RSU Gross Amount', parseFloat(results.rsus || 0)),
      formatListItem('RSU Amount Subject to Income Tax', parseFloat(results.rsus_subject_to_income_tax || 0), null, '', employerNicExplanation),
      formatListItem('Tax Withheld at Vest Time', parseFloat(results.rsu_tax_withheld_at_vest || 0)),
      formatListItem('Your Payslip PAYE Likely Showed', parseFloat(results.estimated_paye_rsu_tax || 0)),
      formatListItem('Calculated Tax Liability', parseFloat(results.rsu_tax || 0)), 
      formatListItem(
        parseFloat(results.rsu_additional_tax_due) > 0 
          ? 'Additional Tax Due via Self-Assessment' 
          : 'Tax Refund Due via Self-Assessment', 
        parseFloat(results.rsu_additional_tax_due) > 0 
          ? parseFloat(results.rsu_additional_tax_due) 
          : parseFloat(results.rsu_refund_due),
        null,
        parseFloat(results.rsu_additional_tax_due) > 0 ? 'negative-value' : 'positive-value'
      )
    ];
  };

  const getRsuEmployerNicExplanation = () => {
    if (!includeRSU) return null;
    
    const isEmployerNicApplied = results.rsu_employer_nic_applied;
    
    return (
      <div style={{ marginTop: 10, marginBottom: 10 }}>
        <Alert
          type={isEmployerNicApplied ? "warning" : "info"}
          message={
            <Text strong>RSU Employer NIC Treatment</Text>
          }
          description={
            isEmployerNicApplied
              ? <Text>Based on your selection, employer NIC is deducted from RSU value before calculating income tax. This reduces the amount subject to income tax but decreases the gross RSU value you receive.</Text>
              : <Text>Based on your selection, employer NIC is NOT deducted from RSU value before calculating income tax. Your full RSU amount is subject to income tax.</Text>
          }
          showIcon
        />
      </div>
    );
  };

  // RSU vest events summary
  const getRsuVestSummary = () => {
    if (!includeRSU || !inputValues.rsuVestEvents || inputValues.rsuVestEvents.length === 0) 
      return [];
      
    return inputValues.rsuVestEvents.map((vest, idx) => {
      return formatListItem(
        `Vest #${idx + 1} (${moment(vest.date).format('DD MMM YYYY')})`, 
        vest.amount, 
        null, 
        'indented'
      );
    });
  };

  const getDetailedRsuTaxBreakdown = () => {
    if (!includeRSU || !inputValues.rsuVestEvents || inputValues.rsuVestEvents.length === 0) return [];
  
    // Create a detailed list showing each vest event's PAYE impact
    return inputValues.rsuVestEvents.map((vest, idx) => {
      const vestDate = moment(vest.date).format('DD MMM YYYY');
      const vestAmount = formatCurrency(vest.amount);
      
      // Use the estimated_paye object if available, otherwise calculate it
      const payeDetails = vest.estimated_paye || {
        paye_amount: vest.amount * (inputValues.isTaxResidentInScotland ? 0.42 : 0.40),
        withholding_amount: vest.amount * (vest.withholding_rate / 100),
        difference: 0 // Will be calculated below
      };
      
      if (!payeDetails.difference) {
        payeDetails.difference = payeDetails.paye_amount - payeDetails.withholding_amount;
      }
      
      const isPotentialRefund = payeDetails.difference < 0;
      const withholding = formatCurrency(payeDetails.withholding_amount);
      const monthOfYear = vest.month_name || moment(vest.date).format('MMM');
      
      // Create a clearer label
      const label = (
        <span>
          <strong>Vest #{idx + 1} ({vestDate})</strong> - {monthOfYear} Payslip
          <br/>
          <span style={{ marginLeft: 20 }}>RSU Amount: {vestAmount}</span>
          <br/>
          <span style={{ marginLeft: 20 }}>Withheld at vest time ({vest.withholding_rate}%): {withholding}</span>
          <br/>
          <span style={{ marginLeft: 20 }}>PAYE calculated: {formatCurrency(payeDetails.paye_amount)}</span>
        </span>
      );
      
      // Use a different approach for formatting the values with correct colors
      const difference = Math.abs(payeDetails.difference);
      
      return {
        label,
        annual: difference,
        monthly: difference / 12,
        className: 'indented',
        isNegativeValue: !isPotentialRefund
      };
    });
  };
  
  // Income breakdown items
  const getIncomeBreakdown = () => {
    return [
      formatListItem('Base Salary', results.base_salary, null, 'indented'),
      formatListItem('Bonus', results.bonus, null, 'indented'),
      formatListItem('RSUs', includeRSU ? results.rsus : 0, null, 'indented'),
    ];
  };

  const getPensionBreakdown = () => {
    return [
      formatListItem('Employee Pension Contribution', parseFloat(results.employee_pension_amount), null, 'indented'),
      formatListItem('Employer Pension Contribution', parseFloat(results.employer_pension_amount), null, 'indented'),
    ]
  }

  const getTaxableIncomeBreakdown = () => {
    return [
      formatListItem('Gross Pay', grossPay, null, 'indented'),
      formatListItem('Less: Employee Pension Contribution', -parseFloat(results.employee_pension_amount), null, 'indented negative-value'),
      includeRSU ? formatListItem('Less: RSU Employer NIC', -(results.rsus - rsuAfterEmployerNIC), null, 'indented negative-value') : null,
      formatListItem('Add: Medical Benefit', parseFloat(inputValues.medicalBenefit), null, 'indented'),
      formatListItem('Personal Allowance', personalAllowance, null, 'indented positive-value'),
    ].filter(Boolean)
  }

  const getTaxCalculationBreakdown = () => {
    return [
      formatListItem('Total Income Tax', incomeTax, null, 'section-total'),
      ...getIncomeTaxBreakdown().map(item => ({ ...item, className: 'indented' })),
      formatListItem('National Insurance Contributions', nic, null, 'section-total'),
      // Add this new line for student loans
      studentLoanRepayments > 0 ? formatListItem('Student Loan Repayments', studentLoanRepayments, null, 'section-total') : null,
      includeRSU ? formatListItem('Capital Gains Tax', parseFloat(results.capital_gains_tax || 0), null, 'section-total') : null,
    ].filter(Boolean);
  };

  // Income tax breakdown by source
  const getIncomeTaxBySource = () => {
    if (!includeRSU) return [];

    return [
      formatListItem('Tax on Base Salary & Bonus', parseFloat(results.non_rsu_tax || 0)),
      formatListItem('Tax on RSUs', parseFloat(results.rsu_tax || 0)),
      formatListItem('Total Income Tax', incomeTax, null, 'section-total')
    ];
  };

  // Organize all the data into logical sections with hierarchy
  const dataSections = [
    {
      title: "Income",
      expandable: true,
      value: grossPay || 0,
      items: [],
      expandedItems: getIncomeBreakdown()
    },
    {
      title: "Pension Contributions",
      expandable: true,
      value: parseFloat(results.total_pension_amount) || 0,
      items: [],
      expandedItems: getPensionBreakdown()
    },
    {
      title: "Taxable Income Calculation",
      expandable: true,
      value: taxableIncome || 0,
      items: [],
      expandedItems: getTaxableIncomeBreakdown()
    },
    {
      title: "Total Deductions & Tax",
      expandable: true,
      value: totalDeductions || 0,
      valueClass: 'negative-value',
      items: [],
      expandedItems: getTaxCalculationBreakdown()
    },
    {
      title: "Take-Home Pay",
      items: [
        formatListItem('Net Salary', netSalary || 0, null, 'section-total positive-value'),
        formatListItem('Net Salary + Pension', netSalaryPlusPension || 0, null, 'section-total positive-value'),
      ]
    },
    {
      title: "Income Tax By Source",
      hidden: !includeRSU,
      expandable: true,
      items: [],
      expandedItems: getIncomeTaxBySource(),
      noValue: true
    },
    {
      title: "Student Loan Repayments",
      hidden: !hasStudentLoans,
      expandable: true,
      items: [],
      expandedItems: Object.entries(studentLoanBreakdown).map(([planType, amount]) => {
        const planName = STUDENT_LOAN_PLAN_NAMES[planType] || `${planType} Plan`;
        return formatListItem(planName, parseFloat(amount), null, 'indented');
      }),
      noValue: true
    },
    {
      title: "RSU Vest Events Summary",
      expandable: true,
      hidden: !includeRSU || !inputValues.rsuVestEvents || inputValues.rsuVestEvents.length === 0,
      items: [],
      expandedItems: getRsuVestSummary(),
      noValue: true
    },
    {
      title: "RSU Tax Summary",
      hidden: !includeRSU,
      expandable: true,
      items: [],
      expandedItems: getRsuTaxSummary(),
      noValue: true
    },
    {
      title: "Monthly Payslip Tax Impact for Each RSU Vest",
      hidden: !includeRSU || !inputValues.rsuVestEvents || inputValues.rsuVestEvents.length === 0,
      expandable: true,
      items: [],
      headerContent: (
        <div style={{ padding: '10px 0', borderBottom: '1px solid #f0f0f0' }}>
          <Text type="secondary">
            This section shows how each RSU vest affected your monthly payslips. The amount shown on the right is the difference between what was withheld at vest time and what HMRC calculated as due on your payslip. Note: the total amounts in this section will differ from the above sections 'Tax Refund Due via Self-Assessment' as this current section is estimating your income through a progressive calculation method (similar to how HMRC does), whereas above section is annual total that you will actually pay given all your inputs.
          </Text>
        </div>
      ),
      expandedItems: getDetailedRsuTaxBreakdown(),
      noValue: true
    }
  ];

  // Find optimal pension % for max take-home pay and personal allowance
  const findOptimalPensionPercentage = () => {
    if (!calculationResults) return null;
    
    // Find the pension % that maximizes the net salary
    const highestNetSalary = calculationResults.reduce((max, row) => {
      const netSalaryField = includeRSU ? 'net_salary' : 'net_salary_excl_rsu';
      const netSalary = parseFloat(row[netSalaryField]);
      return netSalary > max.value ? { value: netSalary, pct: row.employee_pension_contribution_pct } : max;
    }, { value: 0, pct: 0 });
    
    // Find the pension % that maximizes the net salary + pension
    const highestNetSalaryWithPension = calculationResults.reduce((max, row) => {
      const netSalaryPensionField = includeRSU ? 'net_salary_inc_pension' : 'net_salary_inc_pension_ex_RSU';
      const netSalaryPension = parseFloat(row[netSalaryPensionField]);
      return netSalaryPension > max.value ? { value: netSalaryPension, pct: row.employee_pension_contribution_pct } : max;
    }, { value: 0, pct: 0 });
    
    // Find when you get the maximum personal allowance
    const maxPersonalAllowance = 12570; // Max personal allowance amount
    
    // Find the minimum pension % that gives the full personal allowance
    let fullPersonalAllowancePoint = null;
    
    for (let i = 0; i < calculationResults.length; i++) {
      const row = calculationResults[i];
      const allowanceField = includeRSU ? 'personal_allowance_reduction' : 'personal_allowance_reduction_excl_rsus';
      const personalAllowance = parseFloat(row[allowanceField]);
      
      if (Math.abs(personalAllowance - maxPersonalAllowance) < 1) { // If within £1 of max allowance
        fullPersonalAllowancePoint = {
          pct: row.employee_pension_contribution_pct,
          value: personalAllowance
        };
        break;
      }
    }
    
    return {
      netSalary: highestNetSalary,
      netSalaryWithPension: highestNetSalaryWithPension,
      fullPersonalAllowance: fullPersonalAllowancePoint
    };
  };
  
  const optimalPension = findOptimalPensionPercentage();

  const hasSignificantRSUAdjustment = includeRSU && 
    (parseFloat(results.rsu_additional_tax_due) > 100 || parseFloat(results.rsu_refund_due) > 100);

  return (
    <div className="results-table-container">
      <Card title="Tax Breakdown" className="results-card" size="small">       
        {false && optimalPension && (
          <Collapse defaultActiveKey={['1']} ghost className="condensed-collapse">
            <Panel 
              header={
                <Text strong>
                  Optimization Tips <Tag color="blue" style={{ marginLeft: 8, fontSize: '0.8em' }}>Pension Contribution</Tag>
                </Text>
              } 
              key="1"
            >
              {/* Optimization tips temporarily hidden per request */}
            </Panel>
          </Collapse>
        )}

        {hasSignificantRSUAdjustment && (
          <Collapse defaultActiveKey={['1']} ghost className="condensed-collapse">
            <Panel 
              header={
                <Text strong>
                  RSU Tax Adjustment Information <Tag color="orange" style={{ marginLeft: 8, fontSize: '0.8em' }}>Tax Impact</Tag>
                </Text>
              } 
              key="1"
            >
              <div>
                <p><strong>Your RSU tax situation:</strong></p>
                <ul className="compact-list">
                  <li>Your RSUs were withheld at {results.rsu_tax_rate_applied}% on average across all vests</li>
                  <li>Based on your total annual income, the calculator estimates your actual tax rate on these RSUs should be around {parseFloat(results.rsu_actual_tax_rate).toFixed(1)}%</li>
                  <li>Your payslip likely showed a cumulative standard PAYE calculation of approximately {formatCurrency(parseFloat(results.estimated_paye_rsu_tax))}</li>
                  <li>{parseFloat(results.rsu_additional_tax_due) > 0 
                      ? <>You may need to pay an additional <Text type="danger">{formatCurrency(parseFloat(results.rsu_additional_tax_due))}</Text> via Self-Assessment after the tax year ends.</> 
                      : <>You may be eligible for a tax refund of approximately <Text type="success">{formatCurrency(parseFloat(results.rsu_refund_due))}</Text> via Self-Assessment after the tax year ends.</>
                    }</li>
                </ul>
                <p><strong>How RSU tax works:</strong> When RSUs vest, tax is typically withheld at a fixed rate. The difference between this withholding and your actual tax liability is reconciled when you complete your Self-Assessment tax return after the tax year ends.</p>
                <p><em>Note: These calculations are estimates based on the information provided.</em></p>
                <p><strong>Monthly PAYE Impact:</strong> Your monthly payslips will show different tax amounts in months when RSUs vest. The estimated PAYE for each vest event is shown in the breakdown below. {rsuTaxAdjustment > 0 ? 'You may need to pay additional tax' : 'You may be eligible for a refund'} through Self-Assessment after the tax year ends.</p>
              </div>
            </Panel>
          </Collapse>
        )}

        <Row gutter={[16, 4]} align="middle" style={{ marginBottom: '8px' }}>
          <Col>
              <Text strong>Include RSU: </Text>
              <Switch 
                checked={includeRSU} 
                onChange={handleToggleIncludeRsu}
                size="small"
              />
              <Tooltip title="Toggle to see calculations with or without RSUs">
                <InfoCircleOutlined style={{ marginLeft: 8 }} />
              </Tooltip>
          </Col>
        </Row>

        <div className="results-summary">
          {/* Pie chart card commented out per request */}
          {false && (
            <Row gutter={[16, 8]}>
              <Col span={24}>
                <div />
              </Col>
            </Row>
          )}

          {/* Single row of four stat blocks */}
          <Row className="summary-row" gutter={[16, 8]} align="middle">
            <Col xs={12} md={6}>
              <div className="summary-card" style={{ padding: 8, borderRadius: 8, background: 'transparent' }}>
                <Statistic title="Annual Net Salary" value={formatCurrency(netSalary)} valueStyle={{ color: 'black' }} />
              </div>
            </Col>
            <Col xs={12} md={6}>
              <div className="summary-card" style={{ padding: 8, borderRadius: 8, background: 'transparent' }}>
                <Statistic title="Monthly Take-Home" value={formatCurrency(netSalary/12)} valueStyle={{ color: 'black' }} />
              </div>
            </Col>
            <Col xs={12} md={6}>
              <div className="summary-card" style={{ padding: 8, borderRadius: 8, background: 'transparent' }}>
                <Statistic title="Annual Pension Contributions" value={formatCurrency((parseFloat(results.employee_pension_amount||0) + parseFloat(results.employer_pension_amount||0)))} valueStyle={{ color: 'black' }} />
              </div>
            </Col>
            <Col xs={12} md={6}>
              <div className="summary-card" style={{ padding: 8, borderRadius: 8, background: 'transparent' }}>
                <Statistic title="Total RSU Income" value={formatCurrency(includeRSU ? parseFloat(results.rsus||0) : 0)} valueStyle={{ color: 'black' }} />
              </div>
            </Col>
          </Row>
        </div>

        <Divider />

        <div className="section-headers">
          <div className="section-header-label">
            <Text>Item</Text>
          </div>
          <div className="section-header-annual">
            <span className="header-text">Annual</span>
          </div>
          <div className="section-header-monthly">
            <span className="header-text">Monthly</span>
          </div>
        </div>

        {dataSections
          .filter(section => !section.hidden)
          .map((section, sectionIndex) => (
            <div key={`section-${sectionIndex}`} className="data-section">
              {section.title === "RSU Tax Summary" && !section.hidden && getRsuEmployerNicExplanation()}
              <div 
                className={`section-title ${section.expandable ? 'expandable' : ''} ${expandedItems.includes(sectionIndex) ? 'expanded' : ''}`}
                onClick={() => section.expandable && toggleExpand(sectionIndex)}
              >
                <div className="section-header-grid">
                  <div className="section-header-label">
                    <Text strong style={{ display: 'flex', alignItems: 'center' }}>
                      {section.title}
                      {section.expandable && (
                        <>
                          <span style={{ marginLeft: 8 }}>
                            {expandedItems.includes(sectionIndex) 
                              ? <DownOutlined className="expand-icon" /> 
                              : <RightOutlined className="expand-icon" />
                            }
                          </span>
                          <Tag color={expandedItems.includes(sectionIndex) ? "success" : "blue"} style={{ marginLeft: 8, fontSize: '0.8em' }}>
                            {expandedItems.includes(sectionIndex) ? "Click to collapse" : "Click to expand"}
                          </Tag>
                        </>
                      )}
                    </Text>
                  </div>
                  {!section.noValue && section.value !== undefined && (
                    <>
                      <div className="section-header-annual">
                        <span className={`annual-value ${section.valueClass || ''}`}>
                          {formatCurrency(section.value)}
                        </span>
                      </div>
                      <div className="section-header-monthly">
                        <span className={`monthly-value ${section.valueClass || ''}`}>
                          {formatCurrency(section.value / 12)}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
              
              {section.items && section.items.length > 0 && (
                <List
                  className="results-list"
                  size="small"
                  dataSource={section.items}
                  renderItem={(item, itemIndex) => (
                    <List.Item className={`list-item ${item.className || ''}`}>
                      <div className="item-label">
                        <Text className={`label-text ${item.className || ''}`}>
                          {item.label}
                          {item.explanation && (
                            <Tooltip title={item.explanation}>
                              <InfoCircleOutlined style={{ marginLeft: 8, color: '#1890ff' }} />
                            </Tooltip>
                          )}
                        </Text>
                      </div>
                      <div className="item-annual">
                        <span className={`annual-value ${getPositiveNegativeClass(item.annual)}`}>
                          {formatCurrency(item.annual)}
                        </span>
                      </div>
                      <div className="item-monthly">
                        <span className={`monthly-value ${getPositiveNegativeClass(item.monthly)}`}>
                          {formatCurrency(item.monthly)}
                        </span>
                      </div>
                    </List.Item>
                  )}
                />
              )}
              {section.expandable && expandedItems.includes(sectionIndex) && (
                <>
                  {section.headerContent && (
                    <div style={{ padding: '0 12px' }}>
                      {section.headerContent}
                    </div>
                  )}
                  {section.expandedItems && (
                    <List
                      className="results-list"
                      size="small"
                      dataSource={section.expandedItems}
                      renderItem={(item, itemIndex) => (
                        <List.Item className={`list-item ${item.className || ''}`}>
                          <div className="item-label">
                            <Text className={`label-text ${item.className || ''}`}>{item.label}</Text>
                          </div>
                          <div className="item-annual">
                            <span className={item.className || ''}>
                              {item.prefixText && <span>{item.prefixText}</span>}
                              {formatCurrency(item.annual)}
                            </span>
                          </div>
                          <div className="item-monthly">
                            <span className={item.className || ''}>
                              {formatCurrency(item.monthly)}
                            </span>
                          </div>
                        </List.Item>
                      )}
                    />
                  )}
                </>
              )}
            </div>
          ))}
          
        <div className="results-footer">
          <Text type="secondary">
            * All calculations are based on the {selectedTaxYear} tax year rates
            {includeRSU ? ' and include RSU income.' : ' and exclude RSU income.'}
          </Text>
        </div>
      </Card>
    </div>
  );
};

// Simple Statistic component
const Statistic = ({ title, value, valueStyle }) => (
  <div className="statistic">
    <div className="statistic-title">{title}</div>
    <div className="statistic-value" style={valueStyle}>{value}</div>
  </div>
);

export default ResultsTable;